import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { firstValueFrom, from, map, Observable, of, take, timeout } from "rxjs";
import { getAuth } from "firebase/auth";
import { Data } from "@angular/router";
import { LoggingService } from "../services/logging.service";
import { RouteTrackerService } from "../services/route-tracker.service";
import { CommonService } from "../services/common/common.service";
@Injectable({
  providedIn: "root",
})
export class AuthGuard implements CanActivate {
  newGamePageEnabled = false;
  newHomePageEnabled = false;
  private routeConfigRequests = new Map<string, { expiresAt: number; result: Promise<any> }>();
  constructor(private router: Router, private loggingService: LoggingService, private authService: AuthService, private routeTracker: RouteTrackerService, private commonService: CommonService) {}
  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    try {
      const stateUrl = state.url;
      this.routeTracker.setReturnUrl(stateUrl);

      if (stateUrl === "/mainTabs/tabs/game-new") {
        return this.checkNewGamePageAllowed().then((isAllowed) => {
          if (isAllowed) {
            return true;
          } else {
            return this.router.createUrlTree(["/mainTabs/tabs/game"], {
              queryParams: { redirectTo: state.url },
            });
          }
        }).catch((err) => {
          this.loggingService.logError("AuthGuard checkNewGamePageAllowed error", err);
          return this.router.createUrlTree(["/mainTabs/tabs/game"], {
            queryParams: { redirectTo: state.url },
          });
        });
      }
       if (stateUrl === "/mainTabs/tabs/home-scroll") {
        return this.checkNewHomePageAllowed().then((isAllowed) => {
          if (isAllowed) {
            return true;
          } else {
            return this.router.createUrlTree(["/mainTabs/tabs/home"], {
              queryParams: { redirectTo: state.url },
            });
          }
        }).catch((err) => {
          this.loggingService.logError("AuthGuard checkNewHomePageAllowed error", err);
          return this.router.createUrlTree(["/mainTabs/tabs/home"], {
            queryParams: { redirectTo: state.url },
          });
        });
      }
      // Normal /home entry: route members to Home Scroll when pwa_home_experience enables it
      if (this.isClassicHomeRoute(stateUrl)) {
        return this.checkNewHomePageAllowed()
          .then((isAllowed) => {
            if (isAllowed) {
              return this.router.createUrlTree(["/mainTabs/tabs/home-scroll"]);
            }
            return true;
          })
          .catch((err) => {
            this.loggingService.logError("AuthGuard checkNewHomePageAllowed error", err);
            return true;
          });
      }
      const authenticatedUid = this.getAuthenticatedUid();
      return (authenticatedUid ? of(authenticatedUid) : this.authService.getUid$("AuthGuard")).pipe(
        take(1),
        map((userId) => {
          if (!userId || userId === "null" || userId === "undefined") {
            return this.router.createUrlTree(["login"], {
              queryParams: { redirectTo: state.url },
            });
          }
          return true;
        })
      );
    } catch (error) {
      this.loggingService.logError("AuthGuard error", error);

      if (!state.url.includes("/error") && !state.url.includes("/login")) {
        return this.router.createUrlTree(["error"], {
          queryParams: {
            type: "auth",
            redirectTo: state.url,
          },
        });
      }
      return false;
    }
  }

  async checkNewGamePageAllowed(): Promise<boolean> {

   return new Promise((resolve, reject) => {
    const authenticatedUid = this.getAuthenticatedUid();
    (authenticatedUid ? of(authenticatedUid) : this.commonService.getUid("RequestUserService"))
      .pipe(take(1))
      .subscribe({
        next: async (userId) => {
          if (!userId) {
            resolve(false);
            return;
          }
          try {
            const remoteConfigRequest = {
              parameter: "game_new_version",
              condition: "game_new_version_condition",
              feature: "game_new_version",
              userId,
            };

            const res = await this.getRouteConfig(remoteConfigRequest);
            const remoteConfigResponse = res?.data as Data;

            if (remoteConfigResponse && remoteConfigResponse?.game_new_version) {
              this.newGamePageEnabled = remoteConfigResponse.game_new_version;
              resolve(this.newGamePageEnabled);
            } else {
              this.newGamePageEnabled = false;
              resolve(false);
            }
          } catch (err) {
            this.newGamePageEnabled = false;
            reject(err instanceof Error ? err : new Error('Remote config fetch failed'));
          }
        },
        error: (err) => reject(err instanceof Error ? err : new Error('Failed to get user ID')),
      });
  }); 
}
async checkNewHomePageAllowed(): Promise<boolean> {

   return new Promise((resolve, reject) => {
    const authenticatedUid = this.getAuthenticatedUid();
    (authenticatedUid ? of(authenticatedUid) : this.commonService.getUid("RequestUserService"))
      .pipe(take(1))
      .subscribe({
        next: async (userId) => {
          if (!userId) {
            resolve(false);
            return;
          }
          try {
            const remoteConfigRequest = {
              parameter: "pwa_home_experience",
              condition: "pwa_home_experience_condition",
              feature: "home_scroll",
              userId,
            };

            const res = await this.getRouteConfig(remoteConfigRequest);
            const remoteConfigResponse = res?.data as Data;
            if (remoteConfigResponse && remoteConfigResponse?.home_scroll) {
              this.newHomePageEnabled = remoteConfigResponse.home_scroll;
              resolve(this.newHomePageEnabled);
            } else {
              this.newHomePageEnabled = false;
              resolve(false);
            }
          } catch (err) {
            this.newHomePageEnabled = false;
            reject(err instanceof Error ? err : new Error('Remote config fetch failed'));
          }
        },
        error: (err) => reject(err instanceof Error ? err : new Error('Failed to get user ID')),
      });
  });
}

  private getRouteConfig(request: Data): Promise<any> {
    // Parent/child guards and fallback redirects share one bounded decision.
    // The key includes userId, so an account switch never reuses another user's flag.
    const key = JSON.stringify(request);
    const cached = this.routeConfigRequests.get(key);
    if (cached && cached.expiresAt > Date.now()) return cached.result;
    for (const [entryKey, entry] of this.routeConfigRequests) {
      if (entry.expiresAt <= Date.now()) this.routeConfigRequests.delete(entryKey);
    }
    const result = firstValueFrom(from(this.authService.getRemoteConfig(request)).pipe(timeout(2000)));
    this.routeConfigRequests.set(key, { expiresAt: Date.now() + 5000, result });
    return result;
  }

  private getAuthenticatedUid(): string | null {
    // Route shells need established identity, not a fresh Firestore token.
    // Cold auth still uses the existing readiness gates; data callers are unchanged.
    try {
      const user = getAuth().currentUser;
      return user && !user.isAnonymous ? user.uid : null;
    } catch {
      return null;
    }
  }

  /** Match /mainTabs/tabs/home only — not home-feed or home-scroll. */
  private isClassicHomeRoute(url: string): boolean {
    const path = (url || "").split("?")[0].replace(/\/+$/, "");
    return path === "/mainTabs/tabs/home";
  }
}
